import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { injectIntl } from '../../util/reactIntl';
import css from './Modal.css';
import CloseButton from './CloseButton';

const KEY_CODE_ESCAPE = 27;

/**
 * Shared modal shell used by the app's portal-based dialogs.
 *
 * Handles open/close state, escape-key dismissal, scroll locking,
 * sticky footer spacing, and close button rendering.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.scrollLayerClassName]
 * @param {string|string[]} [props.scrollLayerClasses]
 * @param {string} [props.containerClassName]
 * @param {boolean} [props.containerClassNameJoined]
 * @param {string} [props.contentClassName]
 * @param {string} [props.isClosedClassName]
 * @param {boolean} props.isOpen
 * @param {string} [props.cssClass]
 * @param {boolean} [props.isSticky]
 * @param {boolean} [props.disableClosing]
 * @param {string} [props.closeButtonClassName]
 * @param {boolean} [props.lightCloseButton]
 * @param {Function} [props.renderCloseButton]
 * @param {string} [props.id]
 * @param {Function} [props.onManageDisableScrolling]
 * @param {Function} props.onClose
 * @param {string} [props.unsetKey]
 * @param {string|number} [props.step]
 * @param {'contentRef'|'layerRef'} [props.stickyRefNode]
 * @returns {React.ReactElement}
 */
export const ModalComponent = props => {
    const {
        children,
        className,
        scrollLayerClassName,
        scrollLayerClasses,
        containerClassName,
        containerClassNameJoined,
        contentClassName,
        isClosedClassName = css.isClosed,
        isOpen,
        cssClass,
        isSticky,
        disableClosing,
        closeButtonClassName,
        lightCloseButton,
        renderCloseButton,
        id,
        onManageDisableScrolling,
        onClose,
        unsetKey,
        step,
        stickyRefNode = 'contentRef',
    } = props;

    const [scrolled, setScrolled] = useState('unset');

    const contentRef = useRef(null);
    const layerRef = useRef(null);
    const closeBtnRef = useRef(null);

    const stickyNode = stickyRefNode === 'contentRef' ? contentRef : layerRef;

    /** Add extra spacing for sticky footer */
    const addExtraSpacingForBottom = () => {
        if (typeof window !== 'object') return;
        const isMob = window.innerWidth <= 768;

        if (isMob && isSticky && contentRef.current) {
            const footer = contentRef.current.querySelector('footer');
            const height = footer?.clientHeight ?? 120;
            contentRef.current.style.paddingBottom = `${height}px`;
        }
    };

    /** Close handler */
    const handleClose = event => {
        const tagName = (event?.target?.tagName || '').toLowerCase();

        if (tagName !== 'svg' && tagName !== 'span') {
            return;
        }

        onManageDisableScrolling(id, false);
        onClose(event);
    };

    /** Close on ESC */
    const handleBodyKeyUp = event => {
        if (event?.keyCode === KEY_CODE_ESCAPE && isOpen) {
            handleClose(event);
        }
    };

    /** Scroll handler */
    const handleScrollContent = event => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;

        const isTop = scrollTop === 0 && 'top';
        const isBottom = scrollHeight - scrollTop - 1 <= clientHeight && 'bottom';

        setScrolled(isTop || isBottom || 'middle');
    };

    useEffect(() => {
        onManageDisableScrolling(id, isOpen);

        document.body.addEventListener('keyup', handleBodyKeyUp);

        if (stickyNode.current) {
            stickyNode.current.addEventListener('scroll', handleScrollContent);
        }

        addExtraSpacingForBottom();

        return () => {
            document.body.removeEventListener('keyup', handleBodyKeyUp);

            if (stickyNode.current) {
                stickyNode.current.removeEventListener('scroll', handleScrollContent);
            }

            onManageDisableScrolling(id, false);
        };
    }, []);

    /** componentDidUpdate: isOpen changes */
    useEffect(() => {
        onManageDisableScrolling(id, isOpen);
    }, [isOpen]);

    /** unsetKey changed -> reset scroll state */
    useEffect(() => {
        if (unsetKey) {
            setScrolled('unset');
        }
    }, [unsetKey]);

    /** step changed -> re-add spacing */
    useEffect(() => {
        addExtraSpacingForBottom();
    }, [step]);

    const modalClass = isOpen ? css.isOpen : isClosedClassName;
    const classes = classNames(modalClass, className);

    const scrollLayerRootClassName =
        scrollLayerClassName ||
        classNames(css.scrollLayer, {
            [scrollLayerClasses]: !!scrollLayerClasses,
        });

    const containerClasses =
        cssClass ||
        classNames({
            [css.container]: containerClassNameJoined || !containerClassName,
            [css.containerSticky]: isSticky,
            [containerClassName]: !!containerClassName,
        });

    const contentClasses = classNames({
        [css.content]: true,
        [css.contentSticky]: isSticky,
        [contentClassName]: !!contentClassName,
    });

    return (
        <div className={classes}>
            <div className={scrollLayerRootClassName} ref={layerRef}>
                <div className={containerClasses} datatype={`scroll-position-${scrolled}`}>
                    {isOpen && (
                        <CloseButton
                            disableClosing={disableClosing}
                            closeButtonClassName={closeButtonClassName}
                            lightCloseButton={lightCloseButton}
                            scrolled={scrolled}
                            closeBtnRef={closeBtnRef}
                            handleClose={handleClose}
                            renderCloseButton={renderCloseButton}
                        />
                    )}
                    <div className={contentClasses} ref={contentRef}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default injectIntl(ModalComponent);
